import { useState, useEffect } from'react';
import { useParams, useNavigate } from'react-router-dom';
import { motion, AnimatePresence } from'framer-motion';
import { ProgressBar } from'../components/tour/ProgressBar';
import Step1 from'../components/steps/Step1';
import Step2 from'../components/steps/Step2';
import Step3 from'../components/steps/Step3';
import Step4 from'../components/steps/Step4';
import Step5 from'../components/steps/Step5';
import Step6 from'../components/steps/Step6';

const steps = [
 { id: 1, title:'The Vision' },
 { id: 2, title:'The Chariot' },
 { id: 3, title:'The Wheels' },
 { id: 4, title:'The Throne' },
 { id: 5, title:'Seven Heavens' },
 { id: 6, title:'The Ascent' },
];

const stepComponents = [Step1, Step2, Step3, Step4, Step5, Step6];

export default function Demo() {
 const { step } = useParams<{ step?: string }>();
 const navigate = useNavigate();
 const [currentStep, setCurrentStep] = useState(0);

 useEffect(() => {
 const stepNum = step ? parseInt(step, 10) - 1 : 0;
 if (stepNum >= 0 && stepNum <= 5) {
 setCurrentStep(stepNum);
 } else {
 navigate('/demo/1', { replace: true });
 }
 }, [step, navigate]);

 const goToStep = (stepIndex: number) => {
 if (stepIndex >= 0 && stepIndex <= 5) {
 navigate(`/demo/${stepIndex + 1}`);
 }
 };

 const CurrentStepComponent = stepComponents[currentStep];

 return (
 <div className="min-h-screen flex flex-col bg-slate-900">
 {/* The page's own heading. Without it this route had no H1 at all: the only one
     used to be the header wordmark, and demoting that left the page unnamed for a
     reader arriving from search and for anything reading the outline. It names the
     current step, which is what the page is actually showing. */}
 <h1 className="px-2 sm:px-4 lg:px-6 pt-3 text-sm font-semibold text-slate-200">
 Ezekiel's Chariot, step by step: {steps[currentStep].title}
 </h1>
 {/* Progress Bar */}
 <ProgressBar
 steps={steps.map(s => s.title)}
 currentStep={currentStep}
 onStepClick={goToStep}
 />

 {/* Step Content */}
 <div className="flex-1 overflow-y-auto">
 <AnimatePresence mode="wait">
 <motion.div
 key={currentStep}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -20 }}
 transition={{ duration: 0.3 }}
 className="step-layout"
 >
 <CurrentStepComponent />
 </motion.div>
 </AnimatePresence>
 </div>

 {/* Bottom Navigation */}
 <div className="sticky bottom-0 bg-slate-900/95 border-t border-slate-700 px-4 py-3">
 <div className="max-w-4xl mx-auto flex items-center justify-between">
 <button
 onClick={() => goToStep(currentStep - 1)}
 disabled={currentStep === 0}
 className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
 currentStep === 0
 ?'text-slate-600 cursor-not-allowed'
 :'text-slate-300 hover:text-white hover:bg-slate-800'
 }`}
 >
 <span className="text-lg">{'←'}</span>
 <span className="hidden sm:inline">Previous</span>
 </button>

 <div className="text-sm text-slate-500">
 Step {currentStep + 1} of {steps.length}
 </div>

 <button
 onClick={() => goToStep(currentStep + 1)}
 disabled={currentStep === 5}
 className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
 currentStep === 5
 ?'text-slate-600 cursor-not-allowed'
 :'bg-accent-orange text-white hover:bg-orange-600'
 }`}
 >
 <span className="hidden sm:inline">Next</span>
 <span className="text-lg">{'→'}</span>
 </button>
 </div>
 </div>
 </div>
 );
}
